import {useCallback} from 'react'

import type {
  AhpSpaceSelection,
  AhpSubmissionMetadata,
  EstablishedAhpSessionIdentity,
  PreparedPrompt,
} from '@github-ui/ahp-session-client/contracts'
import type {AgentMode} from '@github-ui/copilot-shared-ui/utils/agent-mode'

import {ChatApp, type ChatAppProps} from './ChatApp'
import {clearComposerHandoff} from './runtime/composer-handoff'
import {useConversationDeletedNavigation} from './runtime/use-conversation-deleted-navigation'
import {useComposerHandoffReplay} from './runtime/use-composer-handoff-replay'
import {useMissionControlRuntime} from './runtime/use-mission-control-runtime'
import type {MissionControlRuntimeInput} from './runtime/use-mission-control-runtime'

export interface ChatAutoProvisionRouteProps {
  config: MissionControlRuntimeInput
  initialPrompt?: PreparedPrompt
  /** Structured context selected in the entry-point composer (for example a Space or repository). */
  initialSubmissionMetadata?: AhpSubmissionMetadata
  /** Full Space selection retained as locked composer state after provisioning. */
  initialSelectedSpace?: AhpSpaceSelection
  initialSelectedMode?: AgentMode
  /** Fired once when provisioning resolves identity, before relay startup and first-turn acceptance. */
  onSessionProvisioned?: (identity: EstablishedAhpSessionIdentity) => void
  onSessionCreated?: (taskId: string) => void
  deletionDestination?: string
  renderLayout?: ChatAppProps['renderLayout']
  /**
   * When true, a turn composed on another page (the dashboard composer) is
   * submitted as this chat's first turn. Only the surface the handoff targets
   * opts in, so a chat opened anywhere else never consumes it.
   */
  replayComposerHandoff?: boolean
}

/**
 * Real-UI entry point for the chat-on-sandbox flow. The chat opens immediately
 * and provisions a sandbox lazily — only when the user sends their first prompt.
 * Opening the chat without sending anything provisions nothing.
 */
export function ChatAutoProvisionRoute({
  config,
  initialPrompt,
  initialSubmissionMetadata,
  initialSelectedSpace,
  initialSelectedMode,
  onSessionProvisioned,
  onSessionCreated,
  deletionDestination,
  renderLayout,
  replayComposerHandoff,
}: ChatAutoProvisionRouteProps) {
  const {initialSubmission, establishedAhpSession, onSessionEstablished, onSessionRenamed, onUserRename, debugEvents} =
    useMissionControlRuntime(config, {onSessionProvisioned})
  const conversationDeletion = useConversationDeletedNavigation(config.apiURL, deletionDestination)

  // Mirrors `ChatApp`'s own completion path, which the replay bypasses by
  // dispatching outside the composer: without `onSessionCreated` the new session
  // is never reflected in the URL, so the address bar stays on the bare new-chat
  // route and a reload loses the conversation.
  const handleReplayEstablished = useCallback(
    (session: Parameters<NonNullable<typeof onSessionEstablished>>[0]) => {
      onSessionEstablished?.(session)
      if (session.identity.taskId) onSessionCreated?.(session.identity.taskId)
    },
    [onSessionCreated, onSessionEstablished],
  )

  // The replay claims its turn before submitting, so normally nothing is left
  // behind. This covers the records it never claimed — a turn stored while the
  // destination was unreachable, or one left by an abandoned navigation. Once a
  // session is established here the user has moved on, so replaying such a record
  // on a later visit would submit a turn they did not ask for.
  const handleSessionEstablished = useCallback(
    (session: Parameters<NonNullable<typeof onSessionEstablished>>[0]) => {
      clearComposerHandoff()
      onSessionEstablished?.(session)
    },
    [onSessionEstablished],
  )

  // A handed-off turn provisions this chat exactly as the user's own first turn
  // would, so it rides the same coordinator rather than a parallel path. That
  // also puts it outside the composer's submit path, so a failure has to be
  // handed to the composer explicitly for the user to see it.
  const {
    pending: replayPending,
    prompt: replayPrompt,
    initialSelectedSpace: replaySelectedSpace,
    failure: replayFailure,
  } = useComposerHandoffReplay({
    enabled: Boolean(replayComposerHandoff),
    initialSubmission,
    onSessionEstablished: handleReplayEstablished,
  })

  return (
    <ChatApp
      establishedAhpSession={establishedAhpSession}
      establishedAhpSessionPending={replayPending}
      pendingInitialPrompt={replayPrompt}
      submissionFailure={replayFailure}
      debugEvents={debugEvents}
      onSessionRenamed={onSessionRenamed}
      onUserRename={onUserRename}
      initialSubmission={initialSubmission}
      initialPrompt={initialPrompt}
      initialSubmissionMetadata={initialSubmissionMetadata}
      initialSelectedSpace={initialSelectedSpace ?? replaySelectedSpace}
      initialSelectedMode={initialSelectedMode}
      onSessionEstablished={handleSessionEstablished}
      onSessionCreated={onSessionCreated}
      conversationDeletion={conversationDeletion}
      renderLayout={renderLayout}
      emptyState="immersive"
    />
  )
}

ChatAutoProvisionRoute.displayName = 'ChatAutoProvisionRoute'
